import { useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { parse, stringify } from 'query-string'
import { useDispatch, useSelector } from 'react-redux'

import { setUserForTracking } from 'utils/tracking/set-user-for-tracking'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'
import NewPricing from 'features/pricing'
import { getExperimentComparingPlansCheckout } from 'utils/get-user-super-properties'
import { getUpdatePlanPurchaseType } from 'utils/tracking/calculate-checkout-properties'

import { useGrowthBookFeature } from '../../../context/growthbook-context'
import Modal from '../Modal'
import { CloseButton, OutlineButton } from '../../buttons/Buttons'
import {
  getLoggedUser,
  isEnterpriseAdmin,
  isEnterpriseUser,
  isSignedIn,
} from '../../../utils/userAuth'
import { mediaQueries, palette } from '../../../styles/variables'
import LoginLimitModal from '../LoginLimitModal'
import {
  translate,
  translateFreeTrialWarning,
} from '../../../utils/translation'
import { selectCurrentUser } from '../../../state/selectors'
import { isFreeTrialUsed, isPaidUser } from '../../../utils/freeVersionBlock'
import {
  RecurlyProvider,
  Elements as RecurlyElements,
} from '../../../lib/recurly'
import {
  RECURLY_PUBLIC_KEY,
  getSubscriptionOptions,
} from '../../../utils/payment'
import {
  fetchSubscription,
  fetchUserLimits,
  fetchUserSuccess,
} from '../../../state/actions/global'
import { getUser, updatePlan } from '../../../utils/api'
import { selectProjects } from '../../../containers/Dashboard/selectors'
import { MIXPANEL_PROPS, Mixpanel } from '../../../utils/mixpanel'
import ConfirmationModal from '../ConfirmationModal'
import { isLifetimeValid, sendMessage } from '../../../utils/functions'
import { getDailyReportInfo, setDailyReportInfo } from '../../../utils/storage'
import { fetchProjects } from '../../../containers/Dashboard/actions'
import { growthbookFeatures } from '../../../utils/growthbook'
import { PROMO_LABEL_VALUE } from '../../../utils/consts.mjs'

import SuccessModal from './successModal'
import TrustPilot from './trustpilot'
import AshleeWhiteTestimonial from './testimonials/AshleeWhite'
import LifeTimeTimeLine from './timeLines/LifeTime'
import FreeTrialUsedTimeLine from './timeLines/FreeTrialUsed'
import FreeTrialTimeLine from './timeLines/FreeTrial'
import BfOfferTimeLine from './timeLines/BfOffer'
import YearlyTimeLine from './timeLines/YearlyTime'
import PlanSelection from './planSelection'
import AuthForm from './authForm'
import { NewRecurlyForm, NewCheckoutTimeLine } from './new-recurly-form'

const LeftSection = styled.div(({ height, order, display }) => ({
  minWidth: '390px',
  order,
  display,
  position: 'relative',
  height: 'auto',
  minHeight: height || '640px',
  flexGrow: 1,
  flexDirection: 'column',
  boxSizing: 'border-box',
  backgroundColor: palette.white,
  [mediaQueries.mobile]: {
    minHeight: 'unset',
  },
}))

const RightSection = styled.div(({ height, order, display }) => ({
  flexGrow: 1,
  order,
  display,
  height: 'auto',
  minHeight: height || '640px',
  [mediaQueries.mobile]: {
    minHeight: 'unset',
  },
}))

const RightWrapper = styled.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

const getInitInterval = ({
  currentInterval,
  currentTier,
  growthbook,
  promoFeature,
}) => {
  const defaultPlan = promoFeature?.value?.valid_plans?.find(
    (plan) => plan.default,
  )

  if (growthbook) {
    if (defaultPlan) return defaultPlan.plan_interval
    return currentInterval === 'monthly' && currentTier === 'tier3'
      ? 'yearly'
      : currentInterval || 'yearly'
  }

  return currentInterval === 'monthly' && currentTier === 'tier3'
    ? 'lifetime'
    : currentInterval || 'lifetime'
}

const initInterval = ({
  currentInterval,
  currentTier,
  growthbook,
  user,
  tier,
  promoFeature,
}) => {
  const isLifetimeLeft = isLifetimeValid(user?.lifetime_offer_until)
  const subscriptionOptions = getSubscriptionOptions({
    countryCode: user?.countryCode,
    tier: tier || user?.tier,
    isLifetimeValid: isLifetimeLeft,
    promoFeature,
  })

  const { plan } = parse(window.location.search)

  if (subscriptionOptions.some(({ planLabel }) => planLabel === plan))
    return plan

  const interval = getInitInterval({
    currentInterval,
    currentTier,
    growthbook,
    promoFeature,
  })

  if (
    !subscriptionOptions.some(({ planLabel }) => planLabel === interval) &&
    subscriptionOptions?.length
  ) {
    return subscriptionOptions[0].planLabel
  }

  return interval
}

const initTier = ({ currentTier }) => {
  const { tier } = parse(window.location.search)

  if (tier && (tier === 'tier1' || tier === 'tier2' || tier === 'tier3'))
    return tier

  if (currentTier === 'tier1') return 'tier2'

  if (currentTier === 'tier2') return 'tier3'

  return currentTier === 'tier3' ? 'tier3' : 'tier1'
}

export default ({
  isOpen,
  onClose,
  height,
  title,
  reachedText,
  isAddProjectFlow,
  onCheckoutSuccess,
  maxLimitReachedText,
  shouldOpenATPModal = true,
  ...props
}) => {
  const featureValue = useGrowthBookFeature(growthbookFeatures.PROMO_OFFER)

  const {
    setGwAttributes,
    isLoading: isNewCheckoutPlanSelectionLoading,
    value,
  } = useGrowthBookFeature(growthbookFeatures.NEW_CHECKOUT_PLAN_SELECTION_V2)
  const { isEnabled: isGrowthbookFeatureOn } = featureValue
  const [bannerTheme, setBannerTheme] = useState(
    featureValue?.theme?.promotionLabel || null,
  )
  const user = useSelector(selectCurrentUser())
  const projects = useSelector(selectProjects())
  const currentTier = user?.subscription?.tier
  const currentInterval = user?.subscription?.planInterval
  const [loggedIn, setLoggedIn] = useState(isSignedIn())
  const [isLoginLimitModalOpen, setIsLoginLimitModalOpen] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [warningModal, setWarningModal] = useState({ isOpen: false })
  const [trialEndsWariningModal, setTrialEndsWarningModal] = useState({
    isOpen: false,
  })
  const [previousSubscription, setPreviousSubscription] = useState({
    tier: '',
    interval: '',
  })
  const [step, setStep] = useState('plan_selection')
  const [tier, setTier] = useState(initTier({ currentTier }))
  const [showMobilePaymentForm, setShowMobilePaymentForm] = useState(false)

  const [interval, setInterval] = useState(
    initInterval({
      currentTier,
      currentInterval,
      growthbook: isGrowthbookFeatureOn,
      tier,
      user,
      promoFeature: featureValue,
    }),
  )

  const isMobile = useMediaQuery({ maxWidth: 767 })
  const history = useHistory()
  const dispatch = useDispatch()
  const paymentRef = useRef()
  const contentRef = useRef()

  const handleClose = ({ success } = {}) => {
    const parsed = parse(window.location.search)
    delete parsed.error
    delete parsed.checkout
    delete parsed.tier
    delete parsed.plan
    history.replace({
      search: stringify(parsed),
    })
    !success && onClose?.()
  }

  const loadUser = (cb) => {
    getUser().then((res) => {
      const userInfo = { ...getLoggedUser(), ...res.data }
      localStorage.setItem('user', JSON.stringify(userInfo))
      setUserForTracking(userInfo)
      Mixpanel.setUserProperties(userInfo)
      dispatch(fetchUserSuccess(userInfo))
      dispatch(fetchSubscription())
      dispatch(fetchUserLimits())
      dispatch(fetchProjects())
      const reportInfo = getDailyReportInfo()
      setDailyReportInfo({
        ...reportInfo,
        reports_limits: userInfo.limits.reports || reportInfo?.report_limits,
        visualization_reports_limits:
          userInfo.limits.visualization_reports ||
          reportInfo?.visualization_reports_limits,
      })
      cb?.()
    })
  }

  const handleUpgrade = (cb, token) => {
    const isLifetimeLeft = isLifetimeValid(user.lifetime_offer_until)
    const subscriptionOptions = getSubscriptionOptions({
      countryCode: user.countryCode,
      tier,
      isLifetimeValid: isLifetimeLeft,
      promoFeature: featureValue,
    })
    const currentSubscription = subscriptionOptions.find(
      (subscription) => subscription?.planLabel === interval,
    )
    const { value: subscription } = currentSubscription
    if (
      [PROMO_LABEL_VALUE, 'yearly'].includes(interval) &&
      user?.subscription?.subscriptionStatus === 'trialing'
    ) {
      setTrialEndsWarningModal({
        isOpen: true,
        subscription,
        currentSubscription,
        cb,
      })
    } else {
      performUpdatePlan(subscription, currentSubscription, cb, token)
    }
  }

  const performUpdatePlan = (subscription, currentSubscription, cb, token) => {
    updatePlan(
      subscription,
      currentSubscription?.itemCode,
      undefined,
      token?.id,
    )
      .then((res) => {
        if (res.status === 204) {
          const purchaseType = getUpdatePlanPurchaseType(
            user.subscription.planInterval,
            currentSubscription.plan,
            user.tier,
            currentSubscription.tier,
          )

          trackEvent({
            event: EVENTS.CHECKOUT_SUCCESS,
            mixpanelProperties: [
              {
                tier,
                planInterval: interval,
                paymentMethod:
                  user?.payment_method?.object ||
                  MIXPANEL_PROPS.PAYMENT_METHOD.values.CREDIT_CARD,
                subscription: currentSubscription,
                source: MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
                purchaseType,
              },
            ],
          })
        }
        loadUser(() => {
          setIsSuccessModalOpen(true)
          Mixpanel.trackCongratulationsView(getMixpanelProperties())
        })
      })
      .catch((err) => {
        cb?.(err)
      })
  }

  const handleScrollToTop = () => {
    contentRef?.current?.scrollTo(0, 0)
  }

  const renderTimeline = () => {
    if (step === 'payment')
      return (
        <NewCheckoutTimeLine
          isFreeTrialUsed={isFreeTrialUsed()}
          tier={tier}
          interval={interval}
          onBack={() => setStep('plan_selection')}
          promoFeature={featureValue}
          setShowMobilePaymentForm={setShowMobilePaymentForm}
          isMobile={isMobile}
        />
      )
    if (!loggedIn) {
      return <FreeTrialTimeLine />
    }

    if (interval === PROMO_LABEL_VALUE) {
      return <BfOfferTimeLine />
    }

    if (interval === 'yearly') {
      return <YearlyTimeLine isFreeTrialUsed={isFreeTrialUsed()} />
    }

    if (interval !== 'monthly' && interval !== 'yearly') {
      return <LifeTimeTimeLine />
    }

    if (isFreeTrialUsed()) {
      return <FreeTrialUsedTimeLine />
    }

    return <FreeTrialTimeLine />
  }

  const getMixpanelProperties = () => {
    const isLifetimeLeft = isLifetimeValid(user.lifetime_offer_until)
    const subscriptionOptions = getSubscriptionOptions({
      countryCode: user.countryCode,
      tier,
      isLifetimeValid: isLifetimeLeft,
      promoFeature: featureValue,
    })
    const currentSubscription = subscriptionOptions.find(
      (subscription) => subscription?.planLabel === interval,
    )
    return {
      planTier: tier,
      billingCycle: interval,
      subscription: currentSubscription,
      source: MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
    }
  }

  const renderStep = () => {
    if (!loggedIn) {
      return (
        <AuthForm
          {...props}
          onAuthSuccess={(userResponse) => {
            const currentUserTier = userResponse?.subscription?.tier
            const currentUserInterval = userResponse?.subscription?.planInterval
            setStep('plan_selection')
            const int = initInterval({
              currentTier: currentUserTier,
              currentInterval: currentUserInterval,
              growthbook: isGrowthbookFeatureOn,
              user,
              tier,
              promoFeature: featureValue,
            })
            setInterval(int)
            setTier(initTier({ currentTier: currentUserTier }))
            setLoggedIn(isSignedIn())
          }}
          onClose={handleClose}
          onShowLoginLimitModal={() => setIsLoginLimitModalOpen(true)}
        />
      )
    }

    if (step === 'plan_selection') {
      return (
        <RecurlyProvider publicKey={RECURLY_PUBLIC_KEY}>
          <RecurlyElements>
            <PlanSelection
              interval={interval}
              tier={tier}
              onIntervalChange={setInterval}
              onTierChange={setTier}
              onContinue={() => {
                setStep('payment')
              }}
              onUpgrade={handleUpgrade}
              title={title}
              reachedText={reachedText}
              maxLimitReachedText={maxLimitReachedText}
              promotionLabel={bannerTheme}
              featureValue={featureValue}
              onScrollToTop={handleScrollToTop}
              loggedIn={loggedIn}
            />
          </RecurlyElements>
        </RecurlyProvider>
      )
    }

    return (
      <NewRecurlyForm
        {...props}
        tier={tier}
        interval={interval}
        promoFeature={featureValue}
        onContinue={(cb) => {
          loadUser(() => {
            setIsSuccessModalOpen(true)
            cb?.()
          })
        }}
        onBlockPayment={(params) => {
          setWarningModal({ isOpen: true, ...params })
        }}
        showMobilePaymentForm={showMobilePaymentForm}
        setShowMobilePaymentForm={setShowMobilePaymentForm}
        isMobile={isMobile}
        ref={paymentRef}
        source={MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL}
      />
    )
  }

  useEffect(() => {
    if (user && step === 'plan_selection' && loggedIn) {
      setGwAttributes({ planSelectionNotPaid: !isPaidUser() })
      if (!isNewCheckoutPlanSelectionLoading) {
        Mixpanel.register({
          [MIXPANEL_PROPS.EXPERIMENT_COMPARING_PLANS_CHECKOUT.name]:
            getExperimentComparingPlansCheckout({
              isPaidUser: isPaidUser(),
              enabled: value?.enabled,
              experiment: value?.experiment,
            }),
        })
      }
    }
  }, [
    value?.enabled,
    value?.experiment,
    isNewCheckoutPlanSelectionLoading,
    loggedIn,
    step,
  ])

  useEffect(() => {
    Mixpanel.startRecording()

    Mixpanel.register({
      [MIXPANEL_PROPS.ORIGIN.name]:
        MIXPANEL_PROPS.ORIGIN.values.NEW_COMPONENTS_CHECKOUT_MODAL,
    })

    setPreviousSubscription({
      tier: currentTier,
      interval: currentInterval,
    })
    return () => {
      Mixpanel.stopRecording()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const { plan } = parse(window.location.search)
    if (plan && plan === interval && loggedIn) {
      setStep('payment')
    }
    // eslint-disable-next-line
  }, [loggedIn])

  useEffect(() => {
    if (
      featureValue?.theme?.promotionLabel &&
      !bannerTheme &&
      !featureValue?.isLoading
    ) {
      setBannerTheme(featureValue.theme.promotionLabel)
    }
  }, [featureValue?.theme?.promotionLabel])

  useEffect(() => {
    setInterval(
      initInterval({
        currentTier,
        currentInterval,
        growthbook: isGrowthbookFeatureOn,
        user,
        tier,
        promoFeature: featureValue,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrowthbookFeatureOn])

  const openATPorAIWofferModal = () => {
    if (
      (!previousSubscription.tier ||
        currentTier > previousSubscription.tier ||
        (previousSubscription.interval !== currentInterval &&
          currentTier === previousSubscription.tier)) &&
      !isMobile
    ) {
      sendMessage('MODAL_SUBSCRIPTION_UPGRADED', {
        tier,
        plan: interval,
        paymentMethod: user.subscription.creditCard,
        utmParams: {
          source: 'ubs-account-billing-page',
          medium: 'new-checkout-modal',
        },
      })
      Mixpanel.trackAtpCrossSellModalView(getMixpanelProperties())
      return
    }

    sendMessage('MODAL_SUBSCRIPTION_DOWNGRADED', {
      tier: currentTier,
      utmParams: {
        source: 'ubs-account-billing-page',
        medium: 'new-checkout-modal',
      },
    })
  }

  const getDisplayMobilePaymentSection = (bool) => {
    if (step === 'payment' && isMobile) return bool ? 'flex' : 'none'
    return 'flex'
  }

  const getLeftSectionClass = () => {
    if (step === 'payment') return 'tw-p-4 md:tw-p-6 lg:tw-p-8'
    return isMobile
      ? 'tw-p-[54px_32px_44px_32px]'
      : 'tw-p-[18px_32px_20px_25px]'
  }

  const getRightSectionClass = () => {
    if (step === 'payment')
      return 'tw-w-full md:tw-max-w-[460px] tw-bg-white md:tw-bg-zinc-100 tw-p-4 md:tw-p-6 lg:tw-p-8'
    return isMobile
      ? 'tw-py-12 tw-px-8 flex-grow'
      : 'tw-p-[96px_32px_56px_32px]'
  }

  const getModalStyles = () => {
    if (
      value?.enabled &&
      loggedIn &&
      !isPaidUser() &&
      step === 'plan_selection'
    )
      return {
        maxWidth: '1280px',
        borderRadius: '16px',
        backgroundColor: palette.white,
      }
    if (step === 'payment') return { maxWidth: '1024px' }
    return {}
  }

  return (
    <>
      <LoginLimitModal
        isOpen={isLoginLimitModalOpen}
        type="LOGIN_LIMIT_REACHED"
        title={translate('login_limit_reached')}
        message={translate('login_limit_reached_text')}
        onClose={() => setIsLoginLimitModalOpen(false)}
        onRedirect={handleClose}
      />
      <Modal
        isOpen={isOpen && !isLoginLimitModalOpen && !isSuccessModalOpen}
        desktopStyle={{
          width: '95%',
          height: height || 'auto',
          maxWidth: '900px',
          maxHeight: '90vh',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          position: 'relative',
          inset: 'auto',
          overflowY: 'auto',
          backgroundColor: palette.gray10,
          transition: 'width 0.2s ease, max-width 0.2s ease',
          ...getModalStyles(),
        }}
        mobileStyle={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100svh',
          backgroundColor: palette.gray10,
        }}
        overlayStyle={{
          zIndex: 99999,
        }}
        contentRef={(el) => {
          contentRef.current = el
        }}
        isEnterpriseUser={isEnterpriseUser() || isEnterpriseAdmin()}
        onClose={handleClose}
      >
        <CloseButton
          onClick={handleClose}
          styles={{
            top: '15px',
            zIndex: '999',
          }}
        />
        {loggedIn &&
        !isPaidUser() &&
        value?.enabled &&
        step === 'plan_selection' ? (
          <NewPricing
            interval={interval}
            tier={tier}
            promoFeature={featureValue}
            onContinue={({ interval: newInterval, tier: newTier }) => {
              setInterval(newInterval)
              setTier(newTier)
              setStep('payment')
            }}
          />
        ) : (
          <>
            <LeftSection
              height={height}
              style={isMobile ? { textAlign: 'center', minWidth: '100%' } : {}}
              order={step === 'payment' ? 2 : 0}
              display={getDisplayMobilePaymentSection(showMobilePaymentForm)}
              className={getLeftSectionClass()}
            >
              {renderStep()}
            </LeftSection>
            <RightSection
              height={height}
              order={step === 'payment' ? 1 : 0}
              className={getRightSectionClass()}
              display={getDisplayMobilePaymentSection(!showMobilePaymentForm)}
            >
              <RightWrapper>
                {renderTimeline()}
                {step === 'payment' ? null : (
                  <div style={{ marginTop: '24px' }}>
                    <AshleeWhiteTestimonial />
                    <TrustPilot style={{ marginTop: '24px' }} />
                  </div>
                )}
              </RightWrapper>
            </RightSection>
          </>
        )}
      </Modal>
      <ConfirmationModal
        isOpen={trialEndsWariningModal.isOpen}
        title={translate('confirm_addon_trial_heading')}
        message={translate('confirm_bf_trial_subheading')}
        overlayStyle={{ zIndex: 99999 }}
        onAccept={() => {
          const { subscription, currentSubscription, cb } =
            trialEndsWariningModal
          setTrialEndsWarningModal({ isOpen: false })
          performUpdatePlan(subscription, currentSubscription, cb)
        }}
        onDeny={() => {
          const { cb } = trialEndsWariningModal
          cb?.()
          setTrialEndsWarningModal({ isOpen: false })
        }}
      />
      <ConfirmationModal
        isOpen={warningModal.isOpen}
        title={translate('warning')}
        message={translateFreeTrialWarning(warningModal?.token?.type)}
        overlayStyle={{
          zIndex: 99999,
        }}
        renderFooter={() => {
          return (
            <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-gap-6">
              <OutlineButton
                className="tw-flex-1"
                static
                onClick={() => {
                  paymentRef.current.refresh()
                  setWarningModal({ isOpen: false })
                }}
              >
                {translate('try_new_payment')}
              </OutlineButton>
              <OutlineButton
                className="!tw-ml-0 tw-flex-1"
                static
                onClick={() => {
                  paymentRef.current.continueWithoutFreeTrial({
                    ...warningModal,
                  })
                  setWarningModal({ isOpen: false })
                }}
              >
                {translate('continue_without_trial')}
              </OutlineButton>
            </div>
          )
        }}
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        interval={interval}
        tier={tier}
        onClose={() => {
          const successObject = isAddProjectFlow ? { success: true } : {}
          if (isAddProjectFlow) {
            onCheckoutSuccess()
          }

          handleClose(successObject)
          shouldOpenATPModal && openATPorAIWofferModal()
          if (!projects?.length && !isAddProjectFlow) {
            sendMessage('ADD_PROJECT', {
              eventProperty: {
                addProjectButtonLocation:
                  MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
                    .NEW_CHECKOUT_SUCCESS_MODAL,
              },
            })
          }
        }}
      />
    </>
  )
}
